/**
 * Documents: the receipts and invoices themselves.
 *
 * The uploaded file is `protected` in PocketBase, so it can only be fetched with a short-lived
 * file token - `getFileUrl` takes care of that.
 */
import type { VatLine } from "@/lib/at/vat";
import type {
  DocumentFilters,
  ListState,
  SortDirection,
  SortKey,
} from "@/lib/documents/document-list";
import { hashFile } from "@/lib/utils/hash";
import type {
  CategoriesResponse,
  DocumentsResponse,
  InboundMessagesResponse,
  UsersResponse,
} from "@/lib/platform/pb-types";
import { pb } from "@/lib/platform/pocketbase";

/** What services/ai POST /ai/extract stored in the `extracted` column. */
export type ExtractionPayload = {
  fields: Record<string, unknown>;
  sources: Record<string, string>;
  confidence: number;
  warnings: string[];
  code: { type: "rksv" | "epc" | "unknown"; payload: string } | null;
} | null;

/** A corner of the receipt in its photo, in pixels of the upright original. */
export type ScanCorner = { x: number; y: number };

/** How one photo became one page of the scan PDF (services/ai), stored in `documents.scan`. */
export type ScanPage = {
  /** Top left, top right, bottom right, bottom left; null when the whole photo was kept. */
  corners: ScanCorner[] | null;
  /** Found automatically, or set by a person (false). */
  detected: boolean;
  width: number;
  height: number;
  sourceWidth: number;
  sourceHeight: number;
  sharpness: number;
};

export type DocumentRecord = DocumentsResponse<
  ExtractionPayload,
  ScanPage[] | null,
  VatLine[],
  {
    category?: CategoriesResponse;
    uploadedBy?: UsersResponse;
    inboundMessage?: InboundMessagesResponse;
  }
>;

export type DocumentKind = DocumentRecord["kind"];
export type DocumentStatus = DocumentRecord["status"];

export const DOCUMENT_KINDS: DocumentKind[] = ["ER", "AR", "KA"];
export const DOCUMENT_STATUSES: DocumentStatus[] = ["inbox", "review", "booked", "archived"];

/** Fields the detail form writes back. Everything else is set by upload or by extraction. */
export type DocumentInput = {
  kind: DocumentKind;
  status: DocumentStatus;
  invoiceNumber: string;
  partnerName: string;
  partnerVatId: string;
  partnerAddress: string;
  partnerIban: string;
  issueDate: string;
  serviceDate: string;
  dueDate: string;
  paymentStatus: DocumentRecord["paymentStatus"];
  paymentMethod: DocumentRecord["paymentMethod"];
  paidDate: string;
  currency: string;
  netTotal: number;
  vatTotal: number;
  grossTotal: number;
  vatLines: VatLine[];
  taxTreatment: DocumentRecord["taxTreatment"];
  category: string;
  deductiblePercent: number;
  notes: string;
  /** Trinkgeld on top of the receipt: no VAT, but part of the expense and of the card charge. */
  tip: number;
};

/** Builds the PocketBase filter for the document list. */
export function buildFilter(workspaceId: string, filters: Partial<DocumentFilters> = {}): string {
  const parts = [pb.filter("workspace = {:workspaceId}", { workspaceId })];
  const anyOf = (field: string, values: string[]) =>
    parts.push(
      `(${values.map((value) => pb.filter(`${field} = {:value}`, { value })).join(" || ")})`,
    );

  if (filters.kind && filters.kind !== "all") {
    parts.push(pb.filter("kind = {:kind}", { kind: filters.kind }));
  }
  // Archived documents only show when asked for.
  if (filters.statuses?.length) anyOf("status", filters.statuses);
  else parts.push('status != "archived"');
  if (filters.partners?.length) anyOf("partnerName", filters.partners);
  if (filters.payment)
    parts.push(pb.filter("paymentStatus = {:payment}", { payment: filters.payment }));
  if (filters.treatment)
    parts.push(pb.filter("taxTreatment = {:treatment}", { treatment: filters.treatment }));
  if (filters.category)
    parts.push(pb.filter("category = {:category}", { category: filters.category }));
  if (filters.from)
    parts.push(pb.filter("issueDate >= {:from}", { from: `${filters.from} 00:00:00` }));
  if (filters.to) parts.push(pb.filter("issueDate <= {:to}", { to: `${filters.to} 23:59:59` }));
  if (filters.min) parts.push(pb.filter("grossTotal >= {:min}", { min: Number(filters.min) }));
  if (filters.max) parts.push(pb.filter("grossTotal <= {:max}", { max: Number(filters.max) }));
  if (filters.search?.trim()) {
    const search = filters.search.trim();
    parts.push(
      pb.filter(
        "(partnerName ~ {:search} || invoiceNumber ~ {:search} || docNumber ~ {:search} || notes ~ {:search})",
        { search },
      ),
    );
  }
  return parts.join(" && ");
}

const SORT_FIELDS: Record<SortKey, string> = {
  number: "docNumber",
  date: "issueDate",
  partner: "partnerName",
  gross: "grossTotal",
  status: "status",
};

/** The PocketBase sort for a column; ties fall back to the newest upload. */
export function buildSort(sort?: SortKey, direction: SortDirection = "desc"): string {
  if (!sort) return "-issueDate,-created";
  return `${direction === "desc" ? "-" : ""}${SORT_FIELDS[sort]},-created`;
}

export type DocumentPage = {
  items: DocumentRecord[];
  page: number;
  totalPages: number;
  totalItems: number;
};

/** One page of the Belegjournal. */
export async function listDocumentPage(
  workspaceId: string,
  state: Pick<ListState, "filters" | "sort" | "direction" | "page" | "size">,
): Promise<DocumentPage> {
  const result = await pb.collection("documents").getList<DocumentRecord>(state.page, state.size, {
    filter: buildFilter(workspaceId, state.filters),
    sort: buildSort(state.sort, state.direction),
    expand: "category",
  });
  return {
    items: result.items,
    page: result.page,
    totalPages: result.totalPages,
    totalItems: result.totalItems,
  };
}

/** Every document matching the filters, for the CSV export. */
export async function listDocuments(
  workspaceId: string,
  filters: Partial<DocumentFilters> = {},
  sort?: SortKey,
  direction?: SortDirection,
): Promise<DocumentRecord[]> {
  return pb.collection("documents").getFullList<DocumentRecord>({
    filter: buildFilter(workspaceId, filters),
    sort: buildSort(sort, direction),
    expand: "category",
  });
}

/** The sum of every matching document, not only the page on screen. */
export async function documentsTotal(
  workspaceId: string,
  filters: Partial<DocumentFilters>,
): Promise<number> {
  const rows = await pb.collection("documents").getFullList<Pick<DocumentRecord, "grossTotal">>({
    filter: buildFilter(workspaceId, filters),
    fields: "grossTotal",
  });
  return Math.round(rows.reduce((sum, row) => sum + (row.grossTotal ?? 0) * 100, 0)) / 100;
}

/** The names the workspace's documents carry, A to Z, for the partner filter. */
export async function listPartnerNames(workspaceId: string): Promise<string[]> {
  const rows = await pb.collection("documents").getFullList<Pick<DocumentRecord, "partnerName">>({
    filter: pb.filter('workspace = {:workspaceId} && partnerName != ""', { workspaceId }),
    fields: "partnerName",
  });
  // Exactly as stored: the filter compares with `=`.
  return [...new Set(rows.map((row) => row.partnerName))].sort((a, b) => a.localeCompare(b, "de"));
}

/** Everything in the workspace, for the reports and the year's totals. */
export async function listAllDocuments(workspaceId: string): Promise<DocumentRecord[]> {
  return pb.collection("documents").getFullList<DocumentRecord>({
    filter: pb.filter("workspace = {:workspaceId}", { workspaceId }),
    sort: "-issueDate",
    expand: "category",
  });
}

export async function getDocument(id: string): Promise<DocumentRecord> {
  return pb
    .collection("documents")
    .getOne<DocumentRecord>(id, { expand: "category,uploadedBy,inboundMessage" });
}

export type UploadInput = {
  workspace: string;
  kind: DocumentKind;
  file: File;
  /** Further photos of the same receipt, in page order. */
  pages?: File[];
  /**
   * A scan made on the phone (lib/scan/photo.ts): `file` is its PDF, `originals` the compressed
   * photos and `scan` how each became its page. Without it a photo `file` is its own original.
   */
  originals?: File[];
  scan?: ScanPage[];
  uploadedBy: string;
  /** SHA-256 of `file`, when the caller has computed it already. */
  fileHash?: string;
  /** What was entered at capture time, e.g. at the restaurant table. */
  extra?: Partial<Pick<DocumentInput, "category" | "deductiblePercent" | "notes" | "tip">>;
};

export const isImageFile = (file: File) => file.type.startsWith("image/");

/** The document already holding this exact file (same SHA-256), archived ones included. */
export async function findByFileHash(
  workspaceId: string,
  fileHash: string,
): Promise<Pick<DocumentRecord, "id" | "docNumber"> | null> {
  const page = await pb.collection("documents").getList<DocumentRecord>(1, 1, {
    filter: pb.filter("workspace = {:workspaceId} && fileHash = {:fileHash}", {
      workspaceId,
      fileHash,
    }),
    fields: "id,docNumber",
  });
  return page.items[0] ?? null;
}

export async function uploadDocument(input: UploadInput): Promise<DocumentRecord> {
  const form = new FormData();
  form.set("workspace", input.workspace);
  form.set("kind", input.kind);
  form.set("status", "inbox");
  form.set("ocrStatus", "pending");
  form.set("paymentStatus", input.kind === "KA" ? "paid" : "open");
  form.set("paymentMethod", input.kind === "KA" ? "cash" : "bank_transfer");
  form.set("taxTreatment", "standard");
  form.set("currency", "EUR");
  form.set("deductiblePercent", "100");
  form.set("uploadedBy", input.uploadedBy);
  form.set("originalName", input.file.name);
  form.set("source", "upload");
  // Lets the email inbox recognise the same PDF arriving later by mail.
  form.set("fileHash", input.fileHash ?? (await hashFile(input.file)));
  form.set("file", input.file);
  // Photos are kept as taken: the scan made from them replaces `file`, never these.
  if (input.originals) {
    for (const photo of input.originals) form.append("originals", photo);
    if (input.scan) form.set("scan", JSON.stringify(input.scan));
  } else if (isImageFile(input.file)) {
    for (const photo of [input.file, ...(input.pages ?? [])]) form.append("originals", photo);
  }
  for (const [key, value] of Object.entries(input.extra ?? {})) {
    if (value !== undefined && value !== "") form.set(key, String(value));
  }
  return pb.collection("documents").create<DocumentRecord>(form);
}

export async function updateDocument(
  id: string,
  input: Partial<DocumentInput> &
    Partial<
      Pick<DocumentRecord, "ocrStatus" | "ocrText" | "qrPayload" | "extractionConfidence">
    > & { extracted?: ExtractionPayload; file?: File; scan?: ScanPage[] | null },
): Promise<DocumentRecord> {
  return pb.collection("documents").update<DocumentRecord>(id, input);
}

export async function deleteDocument(id: string): Promise<void> {
  await pb.collection("documents").delete(id);
}

/**
 * URL for the stored file. Protected files need a token, which is valid for a few minutes -
 * long enough to open a preview, short enough that a copied link goes stale.
 */
export async function getFileUrl(
  document: DocumentRecord,
  download = false,
  fileName = document.file,
): Promise<string> {
  const token = await pb.files.getToken();
  return pb.files.getURL(document, fileName, { token, download });
}

/** The photos a scan was made from, as files again - to redo the scan, e.g. with new corners. */
export async function fetchOriginals(document: DocumentRecord): Promise<File[]> {
  return Promise.all(
    (document.originals ?? []).map(async (name) => {
      const blob = await (await fetch(await getFileUrl(document, false, name))).blob();
      return new File([blob], name, { type: blob.type || "image/jpeg" });
    }),
  );
}

export function isImage(document: Pick<DocumentRecord, "file">): boolean {
  return /\.(jpe?g|png|webp|heic|heif)$/i.test(document.file);
}

/** Documents that still need someone to look at them. */
export function needsReview(document: Pick<DocumentRecord, "status">): boolean {
  return document.status === "inbox" || document.status === "review";
}
